import React, { useState, useEffect } from 'react'
import {
  Box, Paper, Table, TableHead, TableBody, TableRow, TableCell, Chip,
  Typography, CircularProgress, IconButton, TextField, InputAdornment, Button
} from '@material-ui/core'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import SearchIcon from '@material-ui/icons/Search'
import moment from 'moment'
import { listCampaigns, getCampaignRecipients, getCampaignStats, getCampaign } from './campaignsApi'
import CampaignReview from './CampaignReview'

const deliveryColor = (s) => {
  switch (s) {
    case 'opened':
    case 'clicked':
    case 'delivered': return { background: '#2a9e0d', color: '#fff' }
    case 'bounced':
    case 'rejected':
    case 'spam': return { background: '#f44336', color: '#fff' }
    case 'soft-bounced':
    case 'deferred': return { background: '#ff9800', color: '#fff' }
    case 'unsub': return { background: '#9e9e9e', color: '#fff' }
    default: return { background: '#e0e0e0', color: '#333' }
  }
}

const statusColor = (s) => (s === 'sent' ? { background: '#779ca4', color: '#fff' }
  : s === 'failed' ? { background: '#f44336', color: '#fff' }
    : { background: '#e0e0e0', color: '#333' })

/**
 * Historical tracking: list of past campaigns, drill into one to see who
 * received it and the delivery/open status of each recipient.
 */
export default function CampaignHistory ({ url }) {
  const [campaigns, setCampaigns] = useState([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState(null)
  const [recipients, setRecipients] = useState([])
  const [stats, setStats] = useState(null)
  const [loadingDetail, setLoadingDetail] = useState(false)
  const [search, setSearch] = useState('')
  const [error, setError] = useState(null)

  const loadList = () => {
    setLoading(true)
    listCampaigns(url)
      .then(setCampaigns)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false))
  }

  useEffect(loadList, [url])

  const openCampaign = async (c) => {
    setSelected(c)
    setLoadingDetail(true)
    setSearch('')
    try {
      const [recs, st] = await Promise.all([
        getCampaignRecipients(url, c._id, true),
        getCampaignStats(url, c._id)
      ])
      setRecipients(recs)
      setStats(st)
    } catch (e) {
      setError(e.response?.data?.error || e.message)
    } finally {
      setLoadingDetail(false)
    }
  }

  if (loading) return <Box p={3}><CircularProgress /></Box>
  if (error) return <Box p={3}><Typography color="error">{error}</Typography></Box>

  // ---- Detail view ----
  if (selected) {
    const filtered = recipients.filter(r => {
      if (!search) return true
      const s = search.toLowerCase()
      return (r.email || '').toLowerCase().includes(s) || (r.companyName || '').toLowerCase().includes(s)
    })
    return (
      <Box>
        <Box display="flex" alignItems="center" mb={1}>
          <IconButton size="small" onClick={() => { setSelected(null); setRecipients([]); setStats(null) }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" style={{ marginLeft: 8 }}>{selected.name}</Typography>
          <Chip size="small" label={selected.status} style={{ marginLeft: 12 }} />
        </Box>
        <Typography variant="body2" color="textSecondary">
          {selected.sentAt ? `Sent ${moment(selected.sentAt).format('MMM D, YYYY h:mm A')}` : 'Not sent'}
          {' · '}Subject: {selected.subject}
        </Typography>

        {stats && (
          <Box my={2} display="flex" style={{ gap: 8, flexWrap: 'wrap' }}>
            <Chip label={`${stats.total} recipients`} />
            <Chip label={`${stats.opened} opened`} style={deliveryColor('opened')} />
            <Chip label={`${stats.opens} total opens`} variant="outlined" />
            <Chip label={`${stats.clicks} clicks`} variant="outlined" />
            {Object.entries(stats.byDelivery || {}).map(([k, v]) => (
              <Chip key={k} size="small" label={`${k}: ${v}`} style={deliveryColor(k)} />
            ))}
          </Box>
        )}

        {/* Draft/tested campaigns are actionable here: preview, test, send. */}
        {(selected.status === 'draft' || selected.status === 'tested') && (
          <Paper variant="outlined" style={{ padding: 16, marginBottom: 16 }}>
            <CampaignReview
              url={url}
              campaign={selected}
              onStatusChange={async () => {
                const fresh = await getCampaign(url, selected._id)
                setSelected(fresh)
                openCampaign(fresh)
              }}
            />
          </Paper>
        )}

        <TextField
          placeholder="Search email or company"
          size="small" variant="outlined" fullWidth
          value={search} onChange={(e) => setSearch(e.target.value)}
          style={{ marginBottom: 12 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
        />

        {loadingDetail ? <CircularProgress /> : (
          <Paper variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Email</TableCell>
                  <TableCell>Company</TableCell>
                  <TableCell>Send</TableCell>
                  <TableCell>Delivery</TableCell>
                  <TableCell align="right">Opens</TableCell>
                  <TableCell>Sent at</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((r) => (
                  <TableRow key={r._id}>
                    <TableCell>{r.email}</TableCell>
                    <TableCell>{r.companyName}</TableCell>
                    <TableCell><Chip size="small" label={r.status} style={statusColor(r.status)} /></TableCell>
                    <TableCell><Chip size="small" label={r.deliveryStatus || '—'} style={deliveryColor(r.deliveryStatus)} /></TableCell>
                    <TableCell align="right">{r.opens || 0}</TableCell>
                    <TableCell>{r.createdAt ? moment(r.createdAt).format('MMM D h:mm A') : ''}</TableCell>
                  </TableRow>
                ))}
                {filtered.length === 0 && (
                  <TableRow><TableCell colSpan={6}><Typography variant="body2" color="textSecondary">No recipients.</Typography></TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        )}
      </Box>
    )
  }

  // ---- List view ----
  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="h6">Campaign History</Typography>
        <Button size="small" onClick={loadList}>Refresh</Button>
      </Box>
      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Campaign</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Sent</TableCell>
              <TableCell align="right">Failed</TableCell>
              <TableCell>Sent at</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {campaigns.map((c) => (
              <TableRow key={c._id} hover style={{ cursor: 'pointer' }} onClick={() => openCampaign(c)}>
                <TableCell>{c.name}</TableCell>
                <TableCell><Chip size="small" label={c.status} /></TableCell>
                <TableCell align="right">{c.stats?.sent ?? 0}</TableCell>
                <TableCell align="right">{c.stats?.failed ?? 0}</TableCell>
                <TableCell>{c.sentAt ? moment(c.sentAt).format('MMM D, YYYY') : '—'}</TableCell>
                <TableCell><Button size="small">View</Button></TableCell>
              </TableRow>
            ))}
            {campaigns.length === 0 && (
              <TableRow><TableCell colSpan={6}><Typography variant="body2" color="textSecondary">No campaigns yet.</Typography></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  )
}
